import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  onAuthStateChanged, signInWithPopup, signInWithEmailAndPassword,
  createUserWithEmailAndPassword, signOut, updateProfile,
  sendEmailVerification, reload, sendPasswordResetEmail,
  setPersistence, browserLocalPersistence, deleteUser
} from "firebase/auth";
import { auth, googleProvider } from "./firebase";
import { getStorageMode } from "./storage";
import { logWarn } from "./utils/logger";

const AuthContext = createContext(null);

// ── FEATURE 2: Per-email login attempt rate limiting ──────────
// Important: lockout must be per email, not global. Otherwise a failed login
// for one tester/user blocks every other account on the same device.
const ATTEMPTS_KEY_PREFIX = "finvault_login_attempts:";
const LOCKOUT_KEY_PREFIX  = "finvault_lockout_until:";
const LEGACY_ATTEMPTS_KEY = "finvault_login_attempts";
const LEGACY_LOCKOUT_KEY  = "finvault_lockout_until";
const MAX_ATTEMPTS = 5;
const LOCKOUT_MINS = 15;
const CLOUD_SESSION_MIGRATION_KEY = "finvault_phase49_cloud_local_persistence_done";

async function prepareCloudAuthSession() {
  // Mobile app behavior: keep Firebase cloud auth in local persistence so closing
  // and reopening the installed PWA/TWA stays signed in. Users can still sign out manually,
  // and PIN/app-lock protects the dashboard after inactivity.
  try {
    await setPersistence(auth, browserLocalPersistence);
  } catch (error) {
    logWarn("FinVault auth local persistence could not be applied", error);
  }
}

function normalizeLoginEmail(email = "") {
  return String(email || "").trim().toLowerCase();
}
function attemptsKey(email) { return ATTEMPTS_KEY_PREFIX + normalizeLoginEmail(email); }
function lockoutKey(email) { return LOCKOUT_KEY_PREFIX + normalizeLoginEmail(email); }
function getAttempts(email) {
  const e = normalizeLoginEmail(email);
  if (!e) return 0;
  return parseInt(localStorage.getItem(attemptsKey(e)) || "0", 10) || 0;
}
function setAttempts(email, n) {
  const e = normalizeLoginEmail(email);
  if (!e) return;
  localStorage.setItem(attemptsKey(e), String(n));
}
function getLockoutUntil(email) {
  const e = normalizeLoginEmail(email);
  if (!e) return 0;
  return parseInt(localStorage.getItem(lockoutKey(e)) || "0", 10) || 0;
}
function setLockoutUntil(email, ts) {
  const e = normalizeLoginEmail(email);
  if (!e) return;
  localStorage.setItem(lockoutKey(e), String(ts));
}
function clearAttempts(email) {
  const e = normalizeLoginEmail(email);
  if (e) {
    localStorage.removeItem(attemptsKey(e));
    localStorage.removeItem(lockoutKey(e));
  }
  // Remove old global lockout keys from older builds so one account cannot
  // continue blocking all other accounts after the update.
  localStorage.removeItem(LEGACY_ATTEMPTS_KEY);
  localStorage.removeItem(LEGACY_LOCKOUT_KEY);
}

export function isLockedOut(email = "") {
  const e = normalizeLoginEmail(email);
  if (!e) return false;
  const until = getLockoutUntil(e);
  if (!until) return false;
  if (Date.now() < until) return true;
  clearAttempts(e);
  return false;
}
export function getLockoutMinutes(email = "") {
  const mins = Math.ceil((getLockoutUntil(email) - Date.now()) / 60000);
  return Math.max(0, mins);
}
export function clearLoginLockout(email = "") {
  clearAttempts(email);
}

// ── FEATURE 4: SHA-256 PIN hash ──────────────────────────────
async function sha256(str) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b=>b.toString(16).padStart(2,"0")).join("");
}
export async function hashPinSHA(pin) { return sha256(pin); }

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const registrationInProgress = useRef(false);

  useEffect(() => {
    const mode = getStorageMode();
    if (mode === "local") {
      setUser({ uid:"local_user", displayName:"Local User", email:"", photoURL:null, isLocal:true });
      setLoading(false);
      return;
    }

    let alive = true;
    let unsub = null;
    const timeout = setTimeout(() => { if (alive) setLoading(false); }, 6000);

    const startAuth = async () => {
      await prepareCloudAuthSession();

      // Phase 49: do not force sign-out on normal app reopen.
      // Earlier session-only auth caused mobile users to log in again every time
      // the installed app was closed and reopened.
      if (localStorage.getItem(CLOUD_SESSION_MIGRATION_KEY) !== "1") {
        localStorage.setItem(CLOUD_SESSION_MIGRATION_KEY, "1");
      }

      if (!alive) return;
      unsub = onAuthStateChanged(auth, async u => {
        clearTimeout(timeout);
        if (!alive) return;
        if (u && !u.emailVerified && u.providerData?.[0]?.providerId === "password" && !registrationInProgress.current) {
          // Email/password user who hasn't verified — sign them out immediately
          await signOut(auth);
          setUser(null);
        } else {
          setUser(u);
        }
        setLoading(false);
      });
    };

    startAuth().catch(error => {
      logWarn("FinVault auth bootstrap failed", error);
      if (alive) setLoading(false);
    });

    return () => { alive = false; clearTimeout(timeout); if (unsub) unsub(); };
  }, []);

  const loginGoogle = async () => {
    await prepareCloudAuthSession();
    return signInWithPopup(auth, googleProvider);
  };

  // ── FEATURE 2: Rate-limited login + email verification check ─
  const loginEmail = async (email, pass) => {
    const normalizedEmail = normalizeLoginEmail(email);
    if (isLockedOut(normalizedEmail)) {
      throw new Error(`Too many failed attempts for this email. Try again in ${getLockoutMinutes(normalizedEmail)} minutes.`);
    }
    try {
      await prepareCloudAuthSession();
      const cred = await signInWithEmailAndPassword(auth, email, pass);
      // ── FEATURE 1: Block access if email not verified
      if (!cred.user.emailVerified) {
        await sendEmailVerification(cred.user); // resend verification
        await signOut(auth);                    // kick them out
        throw new Error("EMAIL_NOT_VERIFIED");
      }
      clearAttempts(normalizedEmail);
      return cred;
    } catch (e) {
      if (e.message === "EMAIL_NOT_VERIFIED") throw e;
      const attempts = getAttempts(normalizedEmail) + 1;
      setAttempts(normalizedEmail, attempts);
      if (attempts >= MAX_ATTEMPTS) {
        setLockoutUntil(normalizedEmail, Date.now() + LOCKOUT_MINS * 60 * 1000);
        throw new Error(`Too many failed attempts for this email. Account locked for ${LOCKOUT_MINS} minutes.`);
      }
      throw new Error(`Invalid email or password. (${MAX_ATTEMPTS - attempts} attempts remaining for this email)`);
    }
  };

  // ── FEATURE 1: Email verification on register ────────────────
  const checkUsernameUnique = async (normalizedUsername) => {
    // Check Firestore for existing username
    try {
      const { collection, doc, getDoc, getDocs, query, where } = await import("firebase/firestore");
      const { db } = await import("./firebase");
      const legacyQuery = query(collection(db, "usernames"), where("username", "==", normalizedUsername));
      const [claim, legacy] = await Promise.all([
        getDoc(doc(db, "usernameClaims", normalizedUsername)),
        getDocs(legacyQuery)
      ]);
      return !claim.exists() && legacy.empty;
    } catch (error) {
      logWarn("Username uniqueness check failed", error);
      // Fail closed: allowing registration here could create duplicate public identities.
      throw new Error("USERNAME_CHECK_UNAVAILABLE");
    }
  };

  const registerEmail = async (email, pass, name, username) => {
    const normalizedUsername = String(username || "").toLowerCase().trim();
    let cred = null;
    let claimCreated = false;
    registrationInProgress.current = true;
    try {
      await prepareCloudAuthSession();
      cred = await createUserWithEmailAndPassword(auth, email, pass);
      await updateProfile(cred.user, { displayName: name });

      if (normalizedUsername) {
        const isUnique = await checkUsernameUnique(normalizedUsername);
        if (!isUnique) throw new Error("USERNAME_TAKEN");

        const { doc, runTransaction, setDoc } = await import("firebase/firestore");
        const { db } = await import("./firebase");
        const claimRef = doc(db, "usernameClaims", normalizedUsername);
        await runTransaction(db, async transaction => {
          const existingClaim = await transaction.get(claimRef);
          if (existingClaim.exists()) throw new Error("USERNAME_TAKEN");
          transaction.set(claimRef, {
            uid: cred.user.uid,
            username: normalizedUsername,
            createdAt: Date.now()
          });
        });
        claimCreated = true;

        await setDoc(doc(db, "usernames", cred.user.uid), {
          username: normalizedUsername,
          displayName: name,
          uid: cred.user.uid,
          createdAt: Date.now()
        });
      }

      await sendEmailVerification(cred.user);
      // ── Sign out immediately — user must verify email before accessing app
      await signOut(auth);
      return cred;
    } catch (e) {
      if (cred?.user) {
        try {
          const { deleteDoc, doc } = await import("firebase/firestore");
          const { db } = await import("./firebase");
          if (claimCreated) await deleteDoc(doc(db, "usernameClaims", normalizedUsername));
          await deleteDoc(doc(db, "usernames", cred.user.uid));
        } catch (cleanupError) {
          logWarn("Registration Firestore rollback failed", cleanupError);
        }
        try {
          await deleteUser(cred.user);
        } catch (cleanupError) {
          logWarn("Registration Auth rollback failed", cleanupError);
          try { await signOut(auth); } catch {}
        }
      }
      // Better error messages
      const msg = e.message || "";
      if (msg.includes("USERNAME_TAKEN") || msg.includes("already-exists")) throw new Error("USERNAME_TAKEN");
      if (msg.includes("USERNAME_CHECK_UNAVAILABLE")) throw e;
      if (msg.includes("email-already-in-use")) throw new Error("This email is already registered. Please sign in instead.");
      if (msg.includes("invalid-email")) throw new Error("Please enter a valid email address.");
      if (msg.includes("weak-password")) throw new Error("Password is too weak. Please use a stronger password.");
      throw e;
    } finally {
      registrationInProgress.current = false;
    }
  };

  const resendVerification = async (email, pass) => {
    // If user is signed in, use current user
    if (auth.currentUser) {
      await sendEmailVerification(auth.currentUser);
      return;
    }
    // Otherwise sign in temporarily to resend
    if (email && pass) {
      try {
        await prepareCloudAuthSession();
        const cred = await signInWithEmailAndPassword(auth, email, pass);
        await sendEmailVerification(cred.user);
        await signOut(auth);
      } catch (e) { throw new Error("Could not resend. Check your email and password."); }
    }
  };

  const checkVerification = async (email, pass) => {
    // If already signed in and verified
    if (auth.currentUser) {
      await reload(auth.currentUser);
      return auth.currentUser.emailVerified;
    }
    // Sign in temporarily to check
    if (email && pass) {
      try {
        await prepareCloudAuthSession();
        const cred = await signInWithEmailAndPassword(auth, email, pass);
        const verified = cred.user.emailVerified;
        if (!verified) await signOut(auth);
        // If verified, stay signed in — onAuthStateChanged will pick it up
        return verified;
      } catch (e) { return false; }
    }
    return false;
  };

  const logout = async () => {
    const mode = getStorageMode();
    if (mode === "local") {
      localStorage.removeItem("finvault_storage_mode");
      window.location.reload();
      return;
    }
    await signOut(auth);
  };

  // ── Password Reset with rate limiting ───────────────────────
  const RESET_KEY        = "finvault_reset_attempts";
  const RESET_LOCK_KEY   = "finvault_reset_lock";
  const MAX_RESET        = 3;   // max 3 attempts
  const RESET_LOCK_MINS  = 30;  // lock for 30 minutes

  const resetPassword = async (email) => {
    if (!email || !email.trim()) throw new Error("Please enter your email address.");

    // ── Check email format first
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) throw new Error("Please enter a valid email address.");

    // ── Check reset rate limit
    const lockUntil = parseInt(localStorage.getItem(RESET_LOCK_KEY) || "0");
    if (Date.now() < lockUntil) {
      const minsLeft = Math.ceil((lockUntil - Date.now()) / 60000);
      throw new Error(`Too many reset attempts. Please try again in ${minsLeft} minutes.`);
    }

    // ── Track attempts
    const attempts = parseInt(localStorage.getItem(RESET_KEY) || "0") + 1;
    localStorage.setItem(RESET_KEY, String(attempts));
    if (attempts >= MAX_RESET) {
      localStorage.setItem(RESET_LOCK_KEY, String(Date.now() + RESET_LOCK_MINS * 60 * 1000));
      localStorage.removeItem(RESET_KEY);
      throw new Error(`Too many attempts. Reset locked for ${RESET_LOCK_MINS} minutes.`);
    }

    try {
      await prepareCloudAuthSession();
      await sendPasswordResetEmail(auth, email.trim(), {
        url: "https://finvault-8ee00.web.app",
        handleCodeInApp: false,
      });
      // ── Success → clear attempts
      localStorage.removeItem(RESET_KEY);
    } catch (e) {
      const msg = e.message || "";
      // ── Always show same success message even for unknown emails
      // This prevents user enumeration (attacker can't tell if email exists)
      if (msg.includes("user-not-found") || msg.includes("invalid-email")) {
        localStorage.removeItem(RESET_KEY); // don't count as attempt
        return; // silently succeed — attacker can't know email doesn't exist
      }
      if (msg.includes("too-many-requests")) throw new Error("Too many requests. Please try again in 30 minutes.");
      throw new Error("Could not send reset email. Please try again.");
    }
  };

  return (
    <AuthContext.Provider value={{
      user, loading,
      loginGoogle, loginEmail, registerEmail,
      resendVerification, checkVerification,
      resetPassword,
      logout
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
